import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ENV_GROUP_LABEL,
  useDevice,
  useDeviceEvents,
  useDeviceGroups,
  useDeviceRegistryInfo,
  useMe,
  useSetDeviceFriendlyName,
} from '../api/hooks';
import { DemoExperience } from '../components/DemoExperience';
import { DeviceLocationCard } from '../components/DeviceLocationCard';
import { DeviceStateTimeline } from '../components/DeviceStateTimeline';
import { DeviceStatus } from '../components/DeviceStatus';
import { UcLogsPanel } from '../components/UcLogsPanel';
import { DeviceUpdateJobs } from '../components/DeviceUpdateJobs';
import { SimCard } from '../components/SimCard';
import { DeviceContentCard } from '../components/DeviceContentCard';
import { DeviceSettings } from '../components/DeviceSettings';
import { DeviceCommandsCatalog } from '../components/DeviceCommandsCatalog';
import { ExportHistoryControl } from '../components/ExportHistoryDialog';
import { fmtAgo, fmtDateHeader, useNow } from '../components/format';
import { fmtSysState, fmtErrState, fmtDemoMode } from '../components/ucEnums';

// Operator-set human label for a device. Renders read-only for
// tenant-viewer; toggles to an inline input + save/cancel on pencil-
// click for tenant-admin and super-admin. RLS rejects cross-tenant
// edits at the backend (rowCount=0 → 404), so a stray tenant-admin can
// see but not save another tenant's device. Empty input clears the name.
function FriendlyNameLine({
  thing,
  friendlyName,
}: {
  thing: string;
  friendlyName: string | null;
}) {
  const me = useMe();
  const canEdit =
    me.data?.role === 'super-admin' || me.data?.role === 'tenant-admin';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(friendlyName ?? '');
  const save = useSetDeviceFriendlyName();

  // Resync draft from server state when not editing, so a refetch that
  // changes the saved name doesn't get masked by a stale local input.
  useEffect(() => {
    if (!editing) setDraft(friendlyName ?? '');
  }, [friendlyName, editing]);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 text-sm mt-1">
        {friendlyName ? (
          <span className="text-fg">{friendlyName}</span>
        ) : (
          <span className="text-muted italic">no friendly name set</span>
        )}
        {canEdit && (
          <button
            onClick={() => setEditing(true)}
            className="text-accent hover:underline text-xs"
            aria-label="Edit name"
            title="Edit friendly name"
          >
            ✎
          </button>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const trimmed = draft.trim();
        await save.mutateAsync({
          thing,
          friendly_name: trimmed === '' ? null : trimmed,
        });
        setEditing(false);
      }}
      className="flex items-center gap-2 mt-1 flex-wrap"
    >
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={80}
        placeholder="Friendly name (max 80 chars; blank to clear)"
        className="px-2 py-1 text-sm border border-border rounded font-sans w-80"
      />
      <button
        type="submit"
        disabled={save.isPending}
        className="text-accent text-xs hover:underline disabled:opacity-50"
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="text-muted text-xs hover:underline"
      >
        Cancel
      </button>
      {save.error && (
        <span className="text-warn text-xs">{(save.error as Error).message}</span>
      )}
    </form>
  );
}

export function DeviceDetail() {
  const { id } = useParams();
  const device = useDevice(id);
  const events = useDeviceEvents(id, 50);
  // DE-134 — env-group lookup so the header pill can show "group: Dev"
  // instead of the (less useful) DB `role` column. DE-178: `useDeviceGroups`
  // is now tenant-scoped, so tenant users get the pill for their own devices
  // too (super-admin sees all); the pill is omitted only when the device
  // isn't in a resolvable group.
  const groups = useDeviceGroups();
  // DE-134 — drive fmtAgo() refresh on a 1 s ticker so "X s ago" updates
  // smoothly even when the device is offline and React Query's structural
  // sharing returns deep-equal data on each 5 s refetch (which would
  // otherwise skip the re-render and freeze the displayed time).
  useNow(1000);
  // DE-132 — when the device row doesn't exist in RDS yet (404), fall back to
  // the IoT registry so we can still show Thing identity + group + certs.
  const not404 = (device.error as { status?: number } | null)?.status !== 404;
  const registryInfo = useDeviceRegistryInfo(id, !not404);

  if (device.isLoading) {
    return <p className="text-sm text-muted">Loading device…</p>;
  }
  if (device.error) {
    const status = (device.error as { status?: number }).status;
    if (status === 404) {
      return (
        <DeviceNotYetPublishing
          id={id}
          info={registryInfo.data}
          loading={registryInfo.isLoading}
          error={registryInfo.error as { status?: number; message?: string } | null}
        />
      );
    }
    return <p className="text-sm text-warn">{(device.error as Error).message}</p>;
  }
  if (!device.data) return null;

  const { device: d } = device.data;
  // DE-134/DE-178 — env-group for this device from the (now tenant-scoped)
  // device-groups view; null if the device isn't in a resolvable group.
  const envGroup =
    groups.data?.groups.find((g) => g.devices.includes(d.thing))?.name ?? null;
  const lastSeen = d.last_seen ? new Date(d.last_seen) : null;
  // DE-134 — `online` now comes from the backend (MQTT lifecycle + 15-min
  // last_seen freshness). Replaces the local last_seen < 5 min inference.
  const online = d.online;
  // Build the tooltip lines that explain *why* the pill is in its state.
  const pillTitle = online
    ? d.last_connected_at
      ? `Connected ${fmtAgo(d.last_connected_at)}`
      : undefined
    : [
        d.last_disconnected_at ? `Disconnected ${fmtAgo(d.last_disconnected_at)}` : null,
        d.last_disconnect_reason ? `Reason: ${d.last_disconnect_reason}` : null,
        lastSeen ? `Last seen ${fmtAgo(lastSeen)}` : null,
      ]
        .filter(Boolean)
        .join('\n') || undefined;
  const shadow = d.shadow_doc as Record<string, Record<string, unknown> | undefined>;
  const hw = (shadow?.device?.hw_ver as string | undefined) ?? undefined;
  const runtime = shadow?.runtime ?? null;
  const projector = shadow?.projector ?? null;
  const sysState =
    runtime && typeof runtime.sys_state === 'number' ? runtime.sys_state : null;
  const errState =
    runtime && typeof runtime.err_state === 'number' ? runtime.err_state : null;
  const demoMode =
    runtime && typeof runtime.demo_mode === 'number' ? runtime.demo_mode : null;
  const projSource =
    projector && typeof projector.source === 'number' ? projector.source : null;
  const hasError = errState !== null && errState !== 0 && errState !== -1;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/" className="text-xs text-muted hover:underline">← back</Link>
        <h1 className="text-2xl font-semibold tracking-tight font-mono mt-1">{d.thing}</h1>
        <FriendlyNameLine thing={d.thing} friendlyName={d.friendly_name} />
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span
            className={online ? 'pill-accent' : 'pill-muted'}
            title={pillTitle}
          >
            {online ? 'online' : 'offline'}
          </span>
          <span className="pill-muted">tenant: {d.tenant_id}</span>
          {envGroup && (
            <span className="pill-muted">group: {ENV_GROUP_LABEL[envGroup]}</span>
          )}
          {/* DE-134 — uC-reported runtime state. These are the only fields
              that go stale the moment the device drops off MQTT, so dim them
              when offline. Identity/group/last-seen stay full-strength. */}
          <div className={`flex items-center gap-2 flex-wrap ${online ? '' : 'opacity-60'}`}>
            {sysState !== null && (
              <span className="pill-muted">sys: {fmtSysState(sysState)}</span>
            )}
            {demoMode !== null && (
              <span className="pill-muted">demo: {fmtDemoMode(demoMode)}</span>
            )}
            {hasError && (
              <span className="pill-accent" title="runtime.err_state">
                err: {fmtErrState(errState)}
              </span>
            )}
          </div>
          {lastSeen && (
            <span className="text-xs text-muted" title={lastSeen.toLocaleString()}>
              last seen {fmtAgo(lastSeen)}
            </span>
          )}
        </div>
      </div>

      {/* DE-136 — engineering telemetry (Metric tiles + history chart) lives on
          /devices/:id/telemetry; the Details page focuses on operational state. */}
      <div className="flex items-center justify-end gap-4">
        <ExportHistoryControl
          thing={d.thing}
          friendlyName={d.friendly_name}
          tenantId={d.tenant_id}
        />
        <Link
          to={`/devices/${encodeURIComponent(d.thing)}/telemetry`}
          className="text-accent text-sm hover:underline"
        >
          View engineering telemetry →
        </Link>
      </div>

      <DeviceLocationCard
        geo={d.geo ?? null}
        thing={d.thing}
        online={d.online}
        lastSeen={d.last_seen}
      />

      <DeviceStateTimeline
        thing={d.thing}
        liveAnchor={{
          sysState,
          demoMode,
          projSource,
          online,
          lastSeen: lastSeen ? lastSeen.getTime() : null,
        }}
      />

      <DemoExperience thing={d.thing} />

      <DeviceStatus shadow={shadow} thing={d.thing} />

      <SimCard thing={d.thing} />

      <DeviceUpdateJobs thing={d.thing} shadow={shadow} envGroup={envGroup} />

      <DeviceContentCard thing={d.thing} envGroup={envGroup} />

      <DeviceSettings thing={d.thing} hw={hw} />

      <UcLogsPanel thing={d.thing} />

      <DeviceCommandsCatalog thing={d.thing} />

      <div className="grid grid-cols-2 gap-4">
        <section id="recent-events" className="card scroll-mt-4">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-medium">Recent events</h2>
            <ExportHistoryControl
              thing={d.thing}
              friendlyName={d.friendly_name}
              tenantId={d.tenant_id}
              label="Export…"
              className="text-accent text-xs hover:underline"
            />
          </div>
          {events.isLoading && <p className="text-sm text-muted">Loading…</p>}
          {events.data && events.data.events.length === 0 && (
            <p className="text-sm text-muted">No events in the recent window.</p>
          )}
          {events.data && events.data.events.length > 0 && (
            <div className="max-h-96 overflow-y-auto -mx-2">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-20 bg-white">
                  <tr className="text-left text-muted border-b border-border">
                    <th className="font-medium px-2 h-7">When</th>
                    <th className="font-medium px-2 h-7">Type</th>
                    <th className="font-medium px-2 h-7">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {events.data.events.map((ev, i, arr) => {
                    // Emit a date band before the first row of each local
                    // day. ev.ts is rendered in local time below, so the
                    // day boundary is computed in local time too
                    // (toDateString) — not UTC.
                    const prev = arr[i - 1];
                    const newDay =
                      !prev ||
                      new Date(prev.ts).toDateString() !==
                        new Date(ev.ts).toDateString();
                    return (
                      <Fragment key={ev.event_id}>
                        {newDay && (
                          <tr>
                            {/* top-7 must match the thead h-7 above so the
                                band pins just under the column header. */}
                            <td
                              colSpan={3}
                              className="sticky top-7 z-10 bg-soft text-muted text-[10px] font-medium uppercase tracking-wide px-2 py-1 border-b border-border"
                            >
                              {fmtDateHeader(ev.ts)}
                            </td>
                          </tr>
                        )}
                        <tr
                          id={`event-${ev.event_id}`}
                          className="border-b border-border last:border-0 scroll-mt-8 transition-colors"
                        >
                          <td className="py-1 px-2 tabular-nums whitespace-nowrap">
                            {new Date(ev.ts).toLocaleTimeString()}
                          </td>
                          <td className="py-1 px-2 font-mono">{ev.event_type}</td>
                          <td className="py-1 px-2 font-mono text-[10px] text-muted truncate max-w-[18ch]">
                            {JSON.stringify(ev.data)}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <h2 className="font-medium mb-3">Shadow document</h2>
          <pre className="text-[10px] font-mono leading-relaxed overflow-x-auto max-h-96 -mx-2 px-2 text-fg">
{JSON.stringify(d.shadow_doc, null, 2)}
          </pre>
        </section>
      </div>
    </div>
  );
}

/**
 * DE-132 — stripped-down view shown when /devices/:id 404s (no RDS row yet).
 * Pulls identity + group + cert info straight from the AWS IoT registry via
 * /admin/devices/:thing/registry-info. Telemetry/shadow sections that depend
 * on a publishing device are replaced with a "not publishing yet" placeholder.
 */
function DeviceNotYetPublishing({
  id,
  info,
  loading,
  error,
}: {
  id: string | undefined;
  info:
    | {
        thing: string;
        arn: string | null;
        thing_type: string | null;
        attributes: Record<string, string>;
        groups: string[];
        env_group: string | null;
        certificates: Array<{ id: string; arn: string }>;
        registry_version: number | null;
      }
    | undefined;
  loading: boolean;
  error: { status?: number; message?: string } | null;
}) {
  if (loading) {
    return <p className="text-sm text-muted">Looking up device in AWS IoT registry…</p>;
  }
  if (error) {
    // Registry fallback also failed — most likely permission (non-super-admin
    // and the Thing has no matching tenant_id) or the Thing genuinely doesn't
    // exist in the registry.
    return (
      <div className="card max-w-md">
        <h2 className="font-semibold mb-1">Device not visible</h2>
        <p className="text-sm text-muted">
          <span className="font-mono">{id}</span> is not visible to your account.
          {error.status === 404 && ' The Thing is not registered in AWS IoT.'}
          {error.status === 403 &&
            " You don't have permission to view this device. Ask a super-admin."}
        </p>
      </div>
    );
  }
  if (!info) return null;
  const envLabel = info.env_group
    ? ENV_GROUP_LABEL[info.env_group as keyof typeof ENV_GROUP_LABEL] ?? info.env_group
    : null;
  return (
    <div className="space-y-6">
      <div>
        <Link to="/" className="text-xs text-muted hover:underline">← back</Link>
        <h1 className="text-2xl font-semibold tracking-tight font-mono mt-1">{info.thing}</h1>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="pill-muted">no telemetry yet</span>
          {envLabel && <span className="pill-muted">env: {envLabel}</span>}
          {info.attributes.tenant_id && (
            <span className="pill-muted">tenant: {info.attributes.tenant_id}</span>
          )}
          {info.attributes.role && (
            <span className="pill-muted">role: {info.attributes.role}</span>
          )}
        </div>
      </div>

      <div className="card max-w-2xl">
        <p className="text-sm text-muted mb-3">
          This device hasn't published any data yet — telemetry, shadow, and
          live status are not available. The information below comes directly
          from the AWS IoT registry. Once the device boots and the
          Greengrass components start publishing, the full Details view will
          replace this placeholder.
        </p>
        {info.env_group === 'artomeui-unassigned' && (
          <p className="text-sm text-muted mb-3">
            <strong>Hint:</strong> this device is in the Unassigned group. The
            initial Greengrass components are deployed there too, so it should
            start publishing within a few minutes of first boot. Move it to{' '}
            <code>dev</code> / <code>demo</code> / <code>prod</code> from the{' '}
            <Link to="/ops/device-groups" className="text-accent hover:underline">
              Groups page
            </Link>{' '}
            once you've decided which environment it belongs in.
          </p>
        )}
        <h3 className="font-medium text-sm mb-2">Registry</h3>
        <div className="space-y-0.5">
          <div className="flex items-baseline gap-3 text-xs py-0.5">
            <span className="text-muted w-32 shrink-0">thing</span>
            <span className="font-mono">{info.thing}</span>
          </div>
          <div className="flex items-baseline gap-3 text-xs py-0.5">
            <span className="text-muted w-32 shrink-0">arn</span>
            <span className="font-mono text-[10px] truncate">{info.arn ?? '—'}</span>
          </div>
          <div className="flex items-baseline gap-3 text-xs py-0.5">
            <span className="text-muted w-32 shrink-0">thing_type</span>
            <span>{info.thing_type ?? '—'}</span>
          </div>
          <div className="flex items-baseline gap-3 text-xs py-0.5">
            <span className="text-muted w-32 shrink-0">version</span>
            <span className="tabular-nums">{info.registry_version ?? '—'}</span>
          </div>
        </div>

        <h3 className="font-medium text-sm mt-4 mb-2">Attributes</h3>
        {Object.keys(info.attributes).length === 0 ? (
          <p className="text-xs text-muted">No attributes set on the Thing.</p>
        ) : (
          <div className="space-y-0.5">
            {Object.entries(info.attributes).map(([k, v]) => (
              <div key={k} className="flex items-baseline gap-3 text-xs py-0.5">
                <span className="text-muted w-32 shrink-0">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        )}

        <h3 className="font-medium text-sm mt-4 mb-2">Groups</h3>
        {info.groups.length === 0 ? (
          <p className="text-xs text-muted">Not a member of any thing group.</p>
        ) : (
          <ul className="text-xs space-y-0.5">
            {info.groups.map((g) => (
              <li key={g} className="font-mono">{g}</li>
            ))}
          </ul>
        )}

        <h3 className="font-medium text-sm mt-4 mb-2">Certificates</h3>
        {info.certificates.length === 0 ? (
          <p className="text-xs text-muted">No certificates attached.</p>
        ) : (
          <ul className="text-xs space-y-0.5">
            {info.certificates.map((c) => (
              <li key={c.id} className="font-mono text-[10px] truncate" title={c.arn}>
                {c.id}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
